import { useState, useCallback } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { VisibilityOutlined, VisibilityOffOutlined } from '@mui/icons-material';
import { useAuth, vortxColors, vortxTypography, retroColors } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';

/**
 * Demo mode toggle — shown in the navbar for demo users.
 *
 * When `is_demo` is true on the user profile:
 * - Shows a "Demo Mode" chip in the navbar
 * - Provides a toggle to switch between demo mode and normal (real data) mode
 * - Toggle calls backend to flip the `is_demo` flag and refreshes the profile
 *
 * When `is_demo` is false or user is not a demo user, renders nothing.
 */
export default function DemoModeToggle() {
    const { userProfile, currentUser, refreshUserProfile } = useAuth();
    const [toggling, setToggling] = useState(false);

    // Only show for demo users (is_demo flag set on the user document)
    const isDemoUser = userProfile?.is_demo === true;
    // Check if the user has the demo_can_toggle permission
    const canToggle = userProfile?.demo_can_toggle === true;

    const handleToggle = useCallback(async () => {
        if (!currentUser || toggling || !canToggle) return;
        setToggling(true);

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/user/demo-mode`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ is_demo: !isDemoUser }),
            });

            if (res.ok) {
                // Refresh profile to get updated is_demo state
                await refreshUserProfile();
            } else {
                console.error('[DemoModeToggle] Failed to toggle demo mode:', res.status);
            }
        } catch (err) {
            console.error('[DemoModeToggle] Error toggling demo mode:', err);
        } finally {
            setToggling(false);
        }
    }, [currentUser, isDemoUser, toggling, canToggle, refreshUserProfile]);

    // Don't render anything if user has no demo flags
    if (!isDemoUser && !canToggle) return null;

    return (
        <Tooltip title={
            canToggle
                ? (isDemoUser ? 'Viewing demo data. Click to switch to your real data.' : 'Viewing real data. Click to switch to demo mode.')
                : 'Demo mode is active. Contact admin to enable toggle.'
        }>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mr: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: isDemoUser ? 'rgba(178, 140, 80, 0.15)' : 'rgba(95, 212, 196, 0.1)',
                border: `1px solid ${isDemoUser ? 'rgba(178, 140, 80, 0.3)' : 'rgba(95, 212, 196, 0.2)'}`,
                cursor: canToggle ? 'pointer' : 'default',
                opacity: toggling ? 0.5 : 1,
                transition: 'all 0.2s ease',
            }}
                onClick={canToggle ? handleToggle : undefined}
            >
                {isDemoUser ? (
                    <VisibilityOutlined sx={{ fontSize: 14, color: vortxColors.gold }} />
                ) : (
                    <VisibilityOffOutlined sx={{ fontSize: 14, color: retroColors.accent }} />
                )}
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.6rem',
                    color: isDemoUser ? vortxColors.gold : retroColors.accent,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}>
                    {isDemoUser ? 'Demo' : 'Live'}
                </Typography>
            </Box>
        </Tooltip>
    );
}
